'use client'

import { useState } from 'react'

import SvgXmark from '@/icons/thin/Xmark'

interface CompareToggleProps {
  isChecked: boolean
  onToggle: () => void
  hidden?: boolean
  /** Translated aria-labels. Defaults keep the original English strings for
   *  callers that don't pass them (the directory grid). */
  addLabel?: string
  removeLabel?: string
}

export const CompareToggle = ({ isChecked, onToggle, hidden, addLabel, removeLabel }: CompareToggleProps) => {
  const [isHovered, setIsHovered] = useState(false)

  if (hidden && !isChecked) {
    return null
  }

  return (
    <button
      type="button"
      data-tracking="ignore"
      aria-label={isChecked ? (removeLabel ?? 'remove from compare') : (addLabel ?? 'add to compare')}
      onClick={onToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="focus-visible:outline-lapis absolute top-0 right-0 z-10 flex w-fit cursor-pointer items-start justify-end rounded-full border-none p-[12px] focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {isChecked && isHovered ? <XCircle /> : isChecked ? <CheckCircle /> : <EmptyCircle />}
    </button>
  )
}

const EmptyCircle = () => (
  <svg
    viewBox="0 0 512 512"
    className="min-h-[24px] min-w-[24px]"
    fill="currentColor"
    style={{ filter: 'invert(51%) sepia(16%) saturate(448%) hue-rotate(182deg) brightness(91%) contrast(86%)' }}
  >
    <path d="M256 16C123.5 16 16 123.5 16 256s107.5 240 240 240 240-107.5 240-240S388.5 16 256 16zm0 464C132 480 32 380 32 256S132 32 256 32s224 100 224 224-100 224-224 224z" />
  </svg>
)

const CheckCircle = () => (
  <svg viewBox="0 0 512 512" className="min-h-[24px] min-w-[24px] fill-lapis">
    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z" />
  </svg>
)

const XCircle = () => (
  <div className="flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200">
    <SvgXmark className="text-navy h-3 w-3" />
  </div>
)
